import type { VideoCompositionSettings } from './videoComposition.ts';

export interface VideoCover {
  work_title?: string;
  theme?: string;
  reference_url?: string;
  references?: Array<{ title: string; url: string }>;
  prompt?: string;
  image_url?: string;
  model?: string;
  generated_at?: string;
}

export interface VideoCreationState {
  cover?: VideoCover;
  cover_scene_id?: string;
  cover_shot_id?: string;
  composition?: VideoCompositionSettings;
}

export interface CoverShotSelection {
  sceneId: string;
  shotId: string;
}

export function selectedCoverShot(document?: Record<string, unknown>): CoverShotSelection | undefined {
  const creation = document?.video_creation as VideoCreationState | undefined;
  const sceneId = String(creation?.cover_scene_id || '');
  const scenes = (Array.isArray(document?.scenes) ? document.scenes : []) as Array<Record<string, unknown>>;
  const matches = scenes.filter(scene => sceneId && String(scene.id || '') === sceneId);
  if (matches.length !== 1) return undefined;
  const shots = (Array.isArray(matches[0].shots) ? matches[0].shots : []) as Array<Record<string, unknown>>;
  // Legacy scene-only links refer to the first shot until the next explicit save.
  const shotId = creation?.cover_shot_id === undefined
    ? String(shots.find(shot => shot.id)?.id || '')
    : String(creation.cover_shot_id || '');
  return shotId && shots.filter(shot => String(shot.id || '') === shotId).length === 1 ? { sceneId, shotId } : undefined;
}

export function withCoverShot(document: Record<string, unknown>, sceneId = '', shotId = ''): Record<string, unknown> {
  const creation = { ...(document.video_creation as VideoCreationState | undefined) };
  if (sceneId || shotId) {
    if (!sceneId || !shotId) throw new Error('封面分镜不存在，请重新选择');
    creation.cover_scene_id = sceneId;
    creation.cover_shot_id = shotId;
  } else {
    delete creation.cover_scene_id;
    delete creation.cover_shot_id;
  }
  const updated = { ...document, video_creation: creation };
  if (sceneId && !selectedCoverShot(updated)) throw new Error('封面分镜不存在，请重新选择');
  return updated;
}

export function reconcileCoverShot(document: Record<string, unknown>): Record<string, unknown> {
  const creation = document.video_creation as VideoCreationState | undefined;
  if (!creation || (creation.cover_scene_id === undefined && creation.cover_shot_id === undefined)) return document;
  const selected = selectedCoverShot(document);
  return selected ? withCoverShot(document, selected.sceneId, selected.shotId) : withCoverShot(document);
}

export function isCoverShot(document: Record<string, unknown> | undefined, sceneId: string, shotId: string): boolean {
  const selected = selectedCoverShot(document);
  return Boolean(selected && selected.sceneId === sceneId && selected.shotId === shotId);
}

export function storyboardKeyframeUrl(document: Record<string, unknown> | undefined, sceneId: string, shot: Record<string, unknown>): string {
  if (isCoverShot(document, sceneId, String(shot.id || ''))) {
    const creation = document?.video_creation as VideoCreationState | undefined;
    return String(creation?.cover?.image_url || '');
  }
  return String(shot.keyframe_url || '');
}
